import { ChevronUp, LogOut, X } from 'lucide-react';
import { useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { cn } from '@/lib/utils';

/**
 * The ONE exit button of the unified room bars (meeting and webinar alike): a single red
 * control whose click opens a small menu - «Выйти» (self) always, and for moderators the
 * destructive «Завершить для всех» behind an inline confirm gate. The confirm sends one
 * `end` moderate frame; the hub broadcasts meeting_ended and deletes the SFU room, and every
 * client lands on the post-room screen. Both bars render this component, so the exit
 * semantics cannot drift between room kinds.
 */
export interface LeaveMenuProps {
  /** True for moderators/admins: the destructive end-for-everyone option renders. */
  readonly canEndForAll: boolean;
  /** True while the end-for-all request is in flight (the option disables). */
  readonly endingForAll: boolean;
  /** Mobile sizing: 36px instead of 40px rows. */
  readonly compact?: boolean;
  readonly onLeave: () => void;
  readonly onEndForAll: () => void;
}

export function LeaveMenu({
  canEndForAll,
  endingForAll,
  compact = false,
  onLeave,
  onEndForAll,
}: LeaveMenuProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [endConfirming, setEndConfirming] = useState(false);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setEndConfirming(false);
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="leave-room"
          aria-label={t('controls.leave')}
          title={t('controls.leave')}
          className={cn(
            'flex shrink-0 items-center gap-1.5 rounded-xl bg-red-500 font-semibold text-white transition-colors hover:bg-red-600',
            compact ? 'h-9 px-2.5 text-[12px]' : 'h-10 px-3.5 text-[12.5px]',
          )}
        >
          <LogOut className="size-4" />
          {t('controls.leave')}
          <ChevronUp className="size-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="end"
        className="min-w-[200px] border-white/10 bg-zinc-900"
      >
        {canEndForAll && endConfirming ? (
          <div data-testid="end-for-all-confirm" className="flex flex-col gap-1.5 px-1 py-0.5">
            <span className="text-[12px] font-medium text-red-200">
              {t('controls.end_for_all_question')}
            </span>
            <div className="flex gap-1.5">
              <button
                type="button"
                data-testid="end-for-all-yes"
                disabled={endingForAll}
                onClick={onEndForAll}
                className="rounded-md bg-red-500/90 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('controls.end_for_all_yes')}
              </button>
              <button
                type="button"
                data-testid="end-for-all-no"
                onClick={() => {
                  setEndConfirming(false);
                }}
                className="rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-zinc-300 transition-colors hover:bg-white/10"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <DropdownMenuItem
              data-testid="leave-meeting"
              className="text-[12.5px]"
              onSelect={onLeave}
            >
              <LogOut className="size-3.5" />
              {t('controls.leave_meeting')}
            </DropdownMenuItem>
            {canEndForAll ? (
              <DropdownMenuItem
                data-testid="end-for-all"
                variant="destructive"
                disabled={endingForAll}
                className="text-[12.5px]"
                onSelect={(event) => {
                  // Keep the menu open for the inline confirm gate.
                  event.preventDefault();
                  setEndConfirming(true);
                }}
              >
                <X className="size-3.5" />
                {t('controls.end_for_all')}
              </DropdownMenuItem>
            ) : null}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
